import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import PageWrapper from '@components/common/layout/PageWrapper';
import Loading from '@components/common/loading';
import { TIME_FORMAT_DISPLAY } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import { showSucsessMessage } from '@services/notifyService';
import { getDetailRestaurantSuccess } from '@store/actions/restaurant';
import { Card } from 'antd';
import moment from 'moment';

import HappyHourSettingForm from './HappyHourSettingForm';

const HappyHourPage = () => {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const { restaurantId } = useParams();
    const [ isGetDetailLoading, setIsGetDetailLoading ] = useState(false);
    const [ dataDetail, setDataDetail ] = useState({});
    const [ isChangedFormValues, setIsChangedFormValues ] = useState(false);

    const getDetail = (restaurantId) => {
        setIsGetDetailLoading(true);
        executeGet({
            pathParams: { id: restaurantId },
            onCompleted: (response) => {
                const mappedData = getDataDetailMapping(response.data);
                setDataDetail(mappedData);
                setIsGetDetailLoading(false);
            },
            onError: () => {
                setIsGetDetailLoading(false);
            },
        });
    };

    const prepareUpdateData = (settingObj) => {
        const newSettingObj = {
            ...dataDetail.settings,
            ...settingObj,
            happy_hours_settings: settingObj.happy_hours_settings || [],
        };
        newSettingObj.happy_hours_settings = newSettingObj.happy_hours_settings.map((happyHour) => {
            const daysOfWeek = [];
            for (let i = 1; i < 8; i++) {
                daysOfWeek.push(happyHour.time_of_weeks.indexOf(i) > -1 ? 1 : 0);
            }
            return {
                timeframe: happyHour.timeframe,
                time_of_weeks: daysOfWeek,
                type: 1,
                from: { hour: happyHour.from.format('HH'), minute: happyHour.from.format('mm') },
                to: { hour: happyHour.to.format('HH'), minute: happyHour.to.format('mm') },
            };
        });
        return { ...dataDetail, settings: JSON.stringify(newSettingObj) };
    };

    const getHappyHoursData = (happyHours) => {
        let happyHourList = [];
        if (!happyHours || happyHours.length < 20) {
            for (let i = 1; i < 21; i++) {
                happyHourList.push({
                    timeframe: `Time frame ${i}`,
                    time_of_weeks: [],
                    from: moment('00:00', TIME_FORMAT_DISPLAY),
                    to: moment('00:00', TIME_FORMAT_DISPLAY),
                });
            }
        } else {
            happyHourList = happyHours.map((happyHour) => {
                const fromHour = happyHour.from.hour || '00';
                const fromMinute = happyHour.from.minute || '00';
                const toHour = happyHour.to.hour || '00';
                const toMinute = happyHour.to.minute || '00';
                let daysOfWeek = [];
                if (happyHour.time_of_weeks && happyHour.time_of_weeks.length > 0) {
                    for (let i = 0; i < 7; i++) {
                        if (happyHour.time_of_weeks[i]) {
                            daysOfWeek.push(i + 1);
                        }
                    }
                }
                return {
                    timeframe: happyHour.timeframe,
                    time_of_weeks: daysOfWeek,
                    from: moment(`${fromHour}:${fromMinute}`, TIME_FORMAT_DISPLAY),
                    to: moment(`${toHour}:${toMinute}`, TIME_FORMAT_DISPLAY),
                };
            });
        }
        return happyHourList;
    };

    const getDataDetailMapping = (data) => {
        const dataDetail = { ...data };
        dataDetail.settings = JSON.parse(data.settings) || {};
        dataDetail.settings.happy_hours_settings = getHappyHoursData(dataDetail.settings.happy_hours_settings);
        return dataDetail;
    };

    const onSave = (values) => {
        const updatedData = prepareUpdateData(values);
        update({
            data: updatedData,
            onCompleted: (res) => {
                showSucsessMessage(translate.formatMessage(commonMessage.updatePrinterSuccess), translate);
                executeGet({
                    pathParams: { id: restaurantId },
                    onCompleted: (response) => {
                        const mappedData = getDataDetailMapping(response.data);
                        setDataDetail(mappedData);
                        dispatch(getDetailRestaurantSuccess({ payload: { data: response.data } }));
                        setIsGetDetailLoading(false);
                    },
                });
            },
            onError: () => {},
        });
    };

    const { execute: executeGet, loading } = useFetch(apiConfig.restaurant.getById, {
        immediate: false,
    });

    const { execute: update, loading: loadingUpdate } = useFetch(apiConfig.restaurant.updateByCustomer);

    useEffect(() => {
        if (restaurantId) {
            getDetail(restaurantId);
        }
    }, [ restaurantId ]);

    const renderActions = () => {
        return [];
    };

    const getFormId = () => {
        return 'happy-hour-setting-form';
    };

    if (isGetDetailLoading) {
        return <Loading />;
    }

    return (
        <PageWrapper loading={loading} routes={[ { breadcrumbName: translate.formatMessage(commonMessage.happyHour) } ]}>
            <div className="basic-form-page">
                <Card
                    title={translate.formatMessage(commonMessage.happyHour)}
                    bordered={false}
                    actions={renderActions()}
                >
                    <HappyHourSettingForm
                        formId={getFormId()}
                        onSubmit={onSave}
                        settingObj={dataDetail.settings || {}}
                        setIsChangedFormValues={setIsChangedFormValues}
                        isChangedFormValues={isChangedFormValues}
                        loadingUpdate={loadingUpdate}
                    />
                </Card>
            </div>
        </PageWrapper>
    );
};

export default HappyHourPage;
